import React, { useState } from 'react';

import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { MICROSOFT_LOGIN_REDIRECT_PATH } from '../../config/apiPaths';
import MicrosoftLogo from '../../icons/MicrosoftLogo';
import { type OauthState } from '../../utils/oauthState';
import { getUrls } from '../../utils/url';

/**
 * Microsoft styling guide for "Sign in with Microsoft"
 * https://learn.microsoft.com/en-us/azure/active-directory/develop/howto-add-branding-in-apps#visual-guidance-for-sign-in
 */
const MicrosoftButton = styled(Button)`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    padding: 8px 12px;
    border-radius: 0;
    margin-bottom: 1rem;
    font-size: 16px;
    font-weight: normal;
    font-family: 'Segoe UI', Helvetica, sans-serif;
    color: ${(props) => props.theme.colors.microsoft.grey};
    border-color: ${(props) => props.theme.colors.microsoft.grey};
    background: ${(props) => props.theme.colors.microsoft.white};

    &:hover {
        border-color: #5e5e5e;
    }
`;

const StyledMicrosoftLogo = styled(MicrosoftLogo)`
    width: 30px;
    height: 30px;
    margin-right: 12px;
`;

const OauthButtonProgress = styled(CircularProgress)`
    position: absolute;
    right: 14px;
    color: ${(props) => props.theme.colors.microsoft.grey};
`;

export interface MicrosoftLoginButtonProps {
    state: OauthState;
    redirect?: (url: string) => void;
}

const MicrosoftLoginButton = (props: MicrosoftLoginButtonProps) => {
    const { t } = useTranslation('login');
    const { api } = getUrls();

    const [loading, setLoading] = useState(false);

    const handleLoginRedirect = () => {
        setLoading(true);

        const params = new URLSearchParams({
            state: JSON.stringify({ api_url: api, ...props.state })
        });

        const redirectUrl = `${api}${MICROSOFT_LOGIN_REDIRECT_PATH}?${params.toString()}`;

        if (props.redirect) {
            props.redirect(redirectUrl);
        } else {
            window.location.href = redirectUrl;
        }
    };

    return (
        <MicrosoftButton disabled={loading} variant="outlined" onClick={() => handleLoginRedirect()}>
            <StyledMicrosoftLogo />
            {t('sign-in-microsoft')}
            {loading && <OauthButtonProgress size={30} />}
        </MicrosoftButton>
    );
};

export default MicrosoftLoginButton;
